import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { Button } from 'antd';
import useHistory from '../../hooks/navigation/useHistory';
import CheckmarkIcon from '../../resources/assets/icons/CheckmarkIcon';
import { ONBOARDING_GUIDE_STEPS } from '../../services/exports/Constants';
import ReduxHooks from '../../store/ReduxHooks';
import { actionCreators } from '../../store/actions';
import { Tabs as TakeoutTabs } from '../../views/order-settings/takeout';
import { Tab as PolygraphyTabs } from '../../views/marketing/polygraphy/products';
import useApiClient from '../../hooks/api/useApiClient';
import { ScreenEnum } from '../../hooks/navigation/Screens';

export default function OnboardingGuideStep({ step, isComplete, onComplete, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Onboarding:OnboardingGuideStep' });

  const { OnboardingStepsManager, FranchisesManager } = useApiClient();
  const history = useHistory();

  const { company } = useSelector((state) => state.currentCompany);

  const [loading, setLoading] = useState(false);

  const navigate = (screen, tab = null, state = {}) => {
    tab && ReduxHooks.dispatch(actionCreators.activeTab.set(tab));
    history.push(screen, state);
  };

  const handlers = {
    [ONBOARDING_GUIDE_STEPS.WhatsApp]: () => navigate(ScreenEnum.Profile),
    [ONBOARDING_GUIDE_STEPS.Voice]: () => navigate(ScreenEnum.Profile),
    [ONBOARDING_GUIDE_STEPS.OpenTimes]: () => navigate(ScreenEnum.OpeningHours),
    [ONBOARDING_GUIDE_STEPS.Location]: () => navigate(ScreenEnum.Locations),
    [ONBOARDING_GUIDE_STEPS.PickupTime]: () => navigate(ScreenEnum.OrderSettingsTakeout, TakeoutTabs.Pickup),
    [ONBOARDING_GUIDE_STEPS.DeliveryTime]: () => navigate(ScreenEnum.OrderSettingsTakeout, TakeoutTabs.Delivery),
    [ONBOARDING_GUIDE_STEPS.OrderSettingsStaffCredentials]: () => navigate(ScreenEnum.OrderSettingsStaffCredentials),
    [ONBOARDING_GUIDE_STEPS.ImportMenu]: () => navigate(ScreenEnum.MenuImport),
    [ONBOARDING_GUIDE_STEPS.Favourites]: () => navigate(ScreenEnum.Menu),
    [ONBOARDING_GUIDE_STEPS.SoldOutItems]: () => navigate(ScreenEnum.SoldOutItems),
    [ONBOARDING_GUIDE_STEPS.PromoCodes]: () => navigate(ScreenEnum.MarketingPromoCodes),
    [ONBOARDING_GUIDE_STEPS.Gallery]: () => navigate(ScreenEnum.MyStoreGallery),
    [ONBOARDING_GUIDE_STEPS.LogoAndPrimaryColor]: () =>
      FranchisesManager.show(company.franchise_id).then(({ data }) =>
        navigate(ScreenEnum.Franchises, null, { selectedFranchise: data }),
      ),
    [ONBOARDING_GUIDE_STEPS.OrderFlyers]: () => navigate(ScreenEnum.MarketingPolygraphyProducts, PolygraphyTabs.edit),
    [ONBOARDING_GUIDE_STEPS.CreateStorefront]: () => navigate(ScreenEnum.Storefront),
    [ONBOARDING_GUIDE_STEPS.EnableLiveOrders]: () => navigate(ScreenEnum.OrderSettingsTakeout, TakeoutTabs.General),
    [ONBOARDING_GUIDE_STEPS.GoogleBusiness]: () => navigate(ScreenEnum.MarketingGoogleBusiness),
  };

  const onClick = () => {
    const handler = handlers[step] ?? null;
    handler && handler();
  };

  const markComplete = async (e) => {
    e.stopPropagation();

    setLoading(true);
    const { success } = await OnboardingStepsManager.markComplete({ step });
    setLoading(false);

    if (!success) {
      return toast.error('toasts.failed_to_save_changes');
    }

    return onComplete();
  };

  return (
    <div
      className={classnames('mt-2 rounded border p-2 cursor-pointer hover:animate-scale', className)}
      onClick={onClick}
    >
      <div className="flex justify-between">
        <div>
          <h6>{t(`titles.${step}`)}</h6>
          <p className="mb-0 mini">{t(`descriptions.${step}`)}</p>
        </div>
        {isComplete && <CheckmarkIcon className="h-5 w-5 my-auto" color="black" />}
      </div>
      {!isComplete && (
        <div className="mt-2 flex justify-end">
          <Button type="primary" loading={loading} onClick={markComplete}>
            {t('buttons.mark_as_complete')}
          </Button>
        </div>
      )}
    </div>
  );
}
